/**
 * useJobWorkflow Hook
 *
 * Manages job workflow state with Firestore persistence.
 * Handles navigation, step completion tracking, and progress calculation.
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { doc, getDoc, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  type JobWorkflowState,
  type WorkflowStepId,
  type WorkflowStepStatus,
  type WorkflowIssue,
  type WorkflowProgress,
  WORKFLOW_STEPS,
  createInitialWorkflowState,
  getWorkflowStep,
  getNextWorkflowStep,
  getPreviousWorkflowStep,
  getIssuesForStep,
  calculateStepStatus,
  hasBlockers,
  isReadyForSubmission,
} from '@/types/workflow';

const DEBOUNCE_MS = 1000;

export interface UseJobWorkflowOptions {
  jobId: string;
  contractorId: string;
  autoLoad?: boolean;
}

export interface UseJobWorkflowReturn {
  // State
  state: JobWorkflowState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;

  // Navigation
  currentStepId: WorkflowStepId;
  currentSubstepId?: string;
  currentStepNumber: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  goToStep: (stepId: WorkflowStepId, substepId?: string) => void;
  goNext: () => void;
  goPrev: () => void;
  goToSubstep: (substepId: string) => void;

  // Step status
  getStepStatus: (stepId: WorkflowStepId) => WorkflowStepStatus;
  isStepComplete: (stepId: WorkflowStepId) => boolean;
  markSubstepComplete: (stepId: WorkflowStepId, substepId: string) => void;
  markSubstepIncomplete: (stepId: WorkflowStepId, substepId: string) => void;

  // Issues
  getIssuesForCurrentStep: () => {
    blockers: WorkflowIssue[];
    warnings: WorkflowIssue[];
    info: WorkflowIssue[];
  };
  setIssues: (blockers: WorkflowIssue[], warnings: WorkflowIssue[], info: WorkflowIssue[]) => void;
  resolveIssue: (issueId: string) => void;

  // Progress
  progress: WorkflowProgress;
  updateProgress: (progress: Partial<WorkflowProgress>) => void;

  // Persistence
  save: () => Promise<void>;
  reload: () => Promise<void>;

  // Helpers
  hasBlockers: boolean;
  isReadyForSubmission: boolean;
  totalSteps: number;
}

export function useJobWorkflow({
  jobId,
  contractorId,
  autoLoad = true,
}: UseJobWorkflowOptions): UseJobWorkflowReturn {
  const { user } = useAuth();
  const { toast } = useToast();

  const [state, setState] = useState<JobWorkflowState | null>(null);
  const [isLoading, setIsLoading] = useState(autoLoad);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Get Firestore document reference
  const getJobRef = useCallback(() => {
    if (!contractorId || !jobId) return null;
    return doc(db, 'contractors', contractorId, 'jobs', jobId);
  }, [contractorId, jobId]);

  // Load workflow state from job document
  const loadWorkflowState = useCallback(async () => {
    const jobRef = getJobRef();
    if (!jobRef || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const jobSnap = await getDoc(jobRef);

      if (!jobSnap.exists()) {
        setError('Job not found');
        setIsLoading(false);
        return;
      }

      const jobData = jobSnap.data();

      // Check if workflow state exists
      if (jobData.workflow) {
        setState(jobData.workflow as JobWorkflowState);
      } else {
        // Initialize workflow state
        const initialState = createInitialWorkflowState(jobId, user.uid);
        setState(initialState);
        // Save initial state to Firestore
        await updateDoc(jobRef, {
          workflow: initialState,
        });
      }

      setIsLoading(false);
    } catch (e) {
      console.error('Failed to load workflow state:', e);
      setError('Failed to load workflow');
      setIsLoading(false);
    }
  }, [getJobRef, jobId, user]);

  // Save workflow state to Firestore
  const saveToFirestore = useCallback(async (workflowState: JobWorkflowState) => {
    const jobRef = getJobRef();
    if (!jobRef) return;

    try {
      await updateDoc(jobRef, {
        workflow: {
          ...workflowState,
          lastUpdatedAt: new Date().toISOString(),
          lastUpdatedBy: user?.uid || 'unknown',
        },
      });
    } catch (e) {
      console.error('Failed to save workflow state:', e);
      throw e;
    }
  }, [getJobRef, user]);

  // Debounced save
  const scheduleSave = useCallback((workflowState: JobWorkflowState) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveToFirestore(workflowState).catch(console.error);
    }, DEBOUNCE_MS);
  }, [saveToFirestore]);

  // Update state with optional auto-save
  const updateState = useCallback((
    updater: (prev: JobWorkflowState) => JobWorkflowState,
    autoSave = true
  ) => {
    setState((prev) => {
      if (!prev) return prev;
      const next = {
        ...updater(prev),
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: user?.uid || 'unknown',
      };
      if (autoSave) {
        scheduleSave(next);
      }
      return next;
    });
    setHasUnsavedChanges(!autoSave);
  }, [user, scheduleSave]);

  // Navigation
  const goToStep = useCallback((stepId: WorkflowStepId, substepId?: string) => {
    const step = getWorkflowStep(stepId);
    if (!step) return;

    updateState((prev) => ({
      ...prev,
      currentStepId: stepId,
      currentSubstepId: substepId || step.substeps[0]?.id,
    }));
  }, [updateState]);

  const goNext = useCallback(() => {
    if (!state) return;

    // Always move to the next step (all substeps are visible on the page at once)
    const nextStep = getNextWorkflowStep(state.currentStepId);
    if (nextStep) {
      updateState((prev) => ({
        ...prev,
        currentStepId: nextStep.id,
        currentSubstepId: nextStep.substeps[0]?.id,
        stepStatus: {
          ...prev.stepStatus,
          [state.currentStepId]: calculateStepStatus(
            state.currentStepId,
            prev.completedSubsteps[state.currentStepId] || [],
            getIssuesForStep(prev, state.currentStepId)
          ),
        },
      }));
    }
  }, [state, updateState]);

  const goPrev = useCallback(() => {
    if (!state) return;

    // Always move to the previous step (all substeps are visible on the page at once)
    const prevStep = getPreviousWorkflowStep(state.currentStepId);
    if (prevStep) {
      updateState((prev) => ({
        ...prev,
        currentStepId: prevStep.id,
        currentSubstepId: prevStep.substeps[0]?.id,
      }));
    }
  }, [state, updateState]);

  const goToSubstep = useCallback((substepId: string) => {
    updateState((prev) => ({
      ...prev,
      currentSubstepId: substepId,
    }));
  }, [updateState]);

  // Step status helpers
  const getStepStatus = useCallback((stepId: WorkflowStepId): WorkflowStepStatus => {
    if (!state) return 'pending';
    return state.stepStatus[stepId] || 'pending';
  }, [state]);

  const isStepComplete = useCallback((stepId: WorkflowStepId): boolean => {
    return getStepStatus(stepId) === 'ready';
  }, [getStepStatus]);

  const markSubstepComplete = useCallback((stepId: WorkflowStepId, substepId: string) => {
    // Track substep completion (for showing checkmarks on individual cards)
    // but DON'T update stepStatus here - that only happens when clicking "Continue"
    // This allows users to click sidebar steps without auto-marking previous steps complete
    updateState((prev) => {
      const existingSubsteps = prev.completedSubsteps[stepId] || [];
      if (existingSubsteps.includes(substepId)) return prev;

      return {
        ...prev,
        completedSubsteps: {
          ...prev.completedSubsteps,
          [stepId]: [...existingSubsteps, substepId],
        },
        // Note: stepStatus is NOT updated here - only in goNext()
      };
    });
  }, [updateState]);

  const markSubstepIncomplete = useCallback((stepId: WorkflowStepId, substepId: string) => {
    // Track substep removal but DON'T update stepStatus here
    updateState((prev) => {
      const existingSubsteps = prev.completedSubsteps[stepId] || [];

      return {
        ...prev,
        completedSubsteps: {
          ...prev.completedSubsteps,
          [stepId]: existingSubsteps.filter((id) => id !== substepId),
        },
        // Note: stepStatus is NOT updated here - only in goNext()
      };
    });
  }, [updateState]);

  // Issues management
  const getIssuesForCurrentStep = useCallback(() => {
    if (!state) return { blockers: [], warnings: [], info: [] };
    return getIssuesForStep(state, state.currentStepId);
  }, [state]);

  const setIssues = useCallback((
    blockers: WorkflowIssue[],
    warnings: WorkflowIssue[],
    info: WorkflowIssue[]
  ) => {
    updateState((prev) => ({
      ...prev,
      blockers,
      warnings,
      info,
      lastComplianceCheckAt: new Date().toISOString(),
    }));
  }, [updateState]);

  const resolveIssue = useCallback((issueId: string) => {
    updateState((prev) => ({
      ...prev,
      blockers: prev.blockers.filter((i) => i.id !== issueId),
      warnings: prev.warnings.filter((i) => i.id !== issueId),
      info: prev.info.filter((i) => i.id !== issueId),
    }));
  }, [updateState]);

  // Progress management
  const updateProgress = useCallback((progress: Partial<WorkflowProgress>) => {
    updateState((prev) => ({
      ...prev,
      progress: {
        ...prev.progress,
        ...progress,
      },
    }));
  }, [updateState]);

  // Manual save
  const save = useCallback(async () => {
    if (!state) return;

    setIsSaving(true);
    try {
      await saveToFirestore(state);
      setHasUnsavedChanges(false);
      toast({
        title: 'Progress saved',
        description: 'Your workflow progress has been saved.',
      });
    } catch (e) {
      toast({
        title: 'Failed to save',
        description: 'Could not save your progress. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [state, saveToFirestore, toast]);

  // Reload state
  const reload = useCallback(async () => {
    await loadWorkflowState();
  }, [loadWorkflowState]);

  // Computed values
  const currentStepNumber = useMemo(() => {
    if (!state) return 1;
    const step = getWorkflowStep(state.currentStepId);
    return step?.number || 1;
  }, [state]);

  const canGoNext = useMemo(() => {
    if (!state) return false;
    const nextStep = getNextWorkflowStep(state.currentStepId);
    return !!nextStep;
  }, [state]);

  const canGoPrev = useMemo(() => {
    if (!state) return false;
    const prevStep = getPreviousWorkflowStep(state.currentStepId);
    return !!prevStep;
  }, [state]);

  const progress = useMemo<WorkflowProgress>(() => {
    if (!state) {
      return {
        dataCompletenessPct: 0,
        packetCompletenessPct: 0,
        complianceReadinessPct: 0,
      };
    }
    return state.progress;
  }, [state]);

  const hasBlockersValue = useMemo(() => {
    return state ? hasBlockers(state) : false;
  }, [state]);

  const isReadyValue = useMemo(() => {
    return state ? isReadyForSubmission(state) : false;
  }, [state]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && !isInitializedRef.current && contractorId && jobId) {
      isInitializedRef.current = true;
      loadWorkflowState();
    }
  }, [autoLoad, contractorId, jobId, loadWorkflowState]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    state,
    isLoading,
    isSaving,
    error,
    hasUnsavedChanges,

    // Navigation
    currentStepId: state?.currentStepId || 'basics',
    currentSubstepId: state?.currentSubstepId,
    currentStepNumber,
    canGoNext,
    canGoPrev,
    goToStep,
    goNext,
    goPrev,
    goToSubstep,

    // Step status
    getStepStatus,
    isStepComplete,
    markSubstepComplete,
    markSubstepIncomplete,

    // Issues
    getIssuesForCurrentStep,
    setIssues,
    resolveIssue,

    // Progress
    progress,
    updateProgress,

    // Persistence
    save,
    reload,

    // Helpers
    hasBlockers: hasBlockersValue,
    isReadyForSubmission: isReadyValue,
    totalSteps: WORKFLOW_STEPS.length,
  };
}

export default useJobWorkflow;
